/* eslint-disable no-constant-condition */
import { axiosInstance } from "@src/api/axiosConfig";
import { initialUserSubsInfo } from "@src/hooks/user/userApi";
import { useRef } from "react";
import { useMutation, useQuery } from "react-query";

const nestPath = process.env.REACT_APP_NEST_API_URL;
const missgoAIPath = `${nestPath}/ai/`;

export const useGetSummaryAuctionAI = (itemId: string, enabled?: boolean) => {
    const fetch = async () => {
        if (!itemId) throw new Error("itemId가 필요합니다");

        const response = await axiosInstance.get(`${missgoAIPath}${itemId}/summary`, {
            timeout: 500000,
        });
        return response?.data;
    };

    return useQuery(["summaryAuctionAI", itemId], fetch, {
        retry: 3,
    });
};

export const useGetFreeWhetherToUse = () => {
    const fetch = async () => {
        const response = await axiosInstance.get(`${missgoAIPath}is-free`);
        return response?.data;
    };

    return useQuery(["freeWhetherToUse"], fetch, {
        retry: 3,
    });
};

export const usePostFreeTrial = () => {
    const mutate = async () => {
        const response = await axiosInstance.post(`${missgoAIPath}free`);
        return response?.data;
    };

    return useMutation(mutate);
};

// AI 챗봇 이전 대화 내역 불러오기
export const useGetBeforeChatLog = (
    itemId: string,
    options?: {
        enabled?: boolean;
        onSuccess?: (data: any) => void;
    },
) => {
    const fetch = async () => {
        if (!itemId) throw new Error("itemId가 필요합니다");
        const response = await axiosInstance.get(`${missgoAIPath}chat/${itemId}`, { timeout: 500000 });
        return response?.data;
    };

    return useQuery(["beforeChatLog", itemId], fetch, {
        staleTime: 1000 * 60 * 5,
        retry: 3,
        enabled: options?.enabled !== undefined ? options.enabled : true,
        onSuccess: options?.onSuccess,
    });
};

// AI 챗봇 질문 보내기
let controller: AbortController | null = null;

export const usePostAIChat = () => {
    const mutateFn = async (data: { itemId: string; question: string }) => {
        controller = new AbortController();
        const response = await axiosInstance.post(`${missgoAIPath}chat`, data, { signal: controller.signal, timeout: 500000 });
        return response?.data;
    };

    const mutation = useMutation(mutateFn);

    const cancel = () => {
        if (controller) {
            controller.abort();
            controller = null;
        }
    };

    return { ...mutation, cancel };
};

// 챗봇 스트리밍 질문 전송
interface StreamRequest {
    itemId: string;
    question: string;
}

interface StreamCallbacks {
    onChunk: (chunk: string) => void;
    onDone?: () => void;
    onError?: (error: Error) => void;
}

let streamController: AbortController | null = null;

export const useGetStreamAIChatAnswer = () => {
    const requestStateRef =
        useRef<{
            itemId: string;
            question: string;
            callbacks: StreamCallbacks;
        } | null>(null);

    const streamFetcher = async () => {
        if (!requestStateRef.current) {
            return;
        }

        const { itemId, question, callbacks } = requestStateRef.current;
        const { onChunk, onDone, onError } = callbacks;

        streamController = new AbortController();
        const decoder = new TextDecoder("utf-8");

        let buffer = "";

        try {
            const url = new URL(`${nestPath}/ai/chat`);
            url.searchParams.append("itemId", itemId);
            url.searchParams.append("question", question);

            const token = localStorage.getItem("accessToken");

            const response = await fetch(url.toString(), {
                method: "GET",
                signal: streamController.signal,
                headers: { Authorization: `Bearer ${token}`, accept: "application/json" },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const stream = response.body;
            if (!stream) {
                throw new Error("ReadableStream not available");
            }

            const reader = stream.getReader();

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    if (buffer.length > 0) {
                        onChunk(buffer);
                    }
                    onDone?.();
                    break;
                }

                const decodedChunk = decoder.decode(value, { stream: true });
                buffer += decodedChunk;

                const dataRegex = /data:\s?(.*)/g;
                let match;
                const dataMessages: string[] = [];
                let lastIndex = 0;

                while ((match = dataRegex.exec(buffer)) !== null) {
                    dataMessages.push(match[1]);
                    lastIndex = match.index + match[0].length;
                }

                if (dataMessages.length > 0) {
                    const combinedMessage = dataMessages.map((s) => (s === "" ? "\n\n" : s)).join("");
                    onChunk(combinedMessage);
                }

                buffer = buffer.slice(lastIndex);
            }
        } catch (error: any) {
            if (error.name === "AbortError") {
                console.log("스트림 요청이 취소되었습니다.");
                onDone?.();
                return;
            }

            if (onError) {
                onError(error);
            } else {
                throw error;
            }
        }
    };

    const queryResult = useQuery<void, Error>(["streamChatAnswer"], streamFetcher, {
        enabled: false,
        retry: false,
        refetchOnWindowFocus: false,
    });

    const startStream = (params: StreamRequest, callbacks: StreamCallbacks) => {
        requestStateRef.current = { ...params, callbacks };
        queryResult.refetch();
    };

    const cancelStream = () => {
        if (streamController) {
            streamController.abort();
        }
    };

    return {
        ...queryResult,
        startStream,
        cancelStream,
        isLoading: queryResult.isFetching,
    };
};

// 챗봇 사용가능 횟수 조회
export const useGetChatAvailableCount = () => {
    const fetch = async () => {
        const response = await axiosInstance.get(`${missgoAIPath}chat/count`, {
            timeout: 500000,
        });
        return response?.data;
    };

    return useQuery(["getChatAvailableCount"], fetch, {
        retry: 3,
    });
};

export const useGetUserSubsInfo = (enabled?: boolean) => {
    const fetch = async () => {
        try {
            const res = await axiosInstance.get(`${nestPath}/user/subs`);
            const msg = (res as any)?.response?.data?.message;
            if (msg?.includes("구독")) return initialUserSubsInfo;
            return res?.data;
        } catch (err: any) {
            const msg = err?.response?.data?.message;
            if (msg?.includes("구독")) return initialUserSubsInfo;
            console.log("err>>>", err);
            return undefined;
        }
    };

    return useQuery(["userSubs"], fetch, {
        enabled,
        retry: 0,
    });
};
